// @figma file=pkCGby54zXzTsccXvHqzO6 node=3791-57238 name="Alert" set="mytaible-shadcn-studio" verified=tbd

import { cva, type VariantProps } from 'class-variance-authority';
import { X } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../lib/utils';

// ─── Base ─────────────────────────────────────────────────────────────────────

export const alertBase =
  'relative w-full rounded-[var(--radius)] px-4 py-3 text-sm [&_svg]:pointer-events-none [&_svg]:shrink-0';

// ─── Variant config ───────────────────────────────────────────────────────────

export const alertVariantConfig = {
  variants: {
    /**
     * Color intent — semantic meaning, not visual style.
     * default     → neutral / generic notice
     * info        → informational hint
     * success     → positive confirmation
     * warning     → caution or advisory
     * destructive → error or critical state
     */
    variant: {
      default: '',
      info: '',
      success: '',
      warning: '',
      destructive: '',
    },
    /**
     * Visual appearance — how saturated the surface is.
     * soft    → subtle tinted bg + muted border (default — recommended for most)
     * solid   → fully filled bg with foreground text (high emphasis)
     * outline → transparent bg + colored border (medium emphasis)
     * ghost   → tinted bg, no border (lowest emphasis)
     */
    appearance: {
      solid: '',
      soft: 'border',
      outline: 'border bg-transparent',
      ghost: '',
    },
  },

  compoundVariants: [
    // ── Solid ──────────────────────────────────────────────────────────────────
    {
      variant: 'default' as const,
      appearance: 'solid' as const,
      class: 'bg-secondary border-secondary text-secondary-foreground',
    },
    {
      variant: 'info' as const,
      appearance: 'solid' as const,
      class: 'bg-info border-info text-info-foreground',
    },
    {
      variant: 'success' as const,
      appearance: 'solid' as const,
      class: 'bg-success border-success text-success-foreground',
    },
    {
      variant: 'warning' as const,
      appearance: 'solid' as const,
      class: 'bg-warning border-warning text-warning-foreground',
    },
    {
      variant: 'destructive' as const,
      appearance: 'solid' as const,
      class: 'bg-destructive border-destructive text-destructive-foreground',
    },

    // ── Soft ───────────────────────────────────────────────────────────────────
    {
      variant: 'default' as const,
      appearance: 'soft' as const,
      class: 'bg-muted text-foreground border-border',
    },
    {
      variant: 'info' as const,
      appearance: 'soft' as const,
      class: 'bg-info/10 border-info/20 text-info',
    },
    {
      variant: 'success' as const,
      appearance: 'soft' as const,
      class: 'bg-success/10 border-success/20 text-success',
    },
    {
      variant: 'warning' as const,
      appearance: 'soft' as const,
      class: 'bg-warning/10 border-warning/20 text-warning',
    },
    {
      variant: 'destructive' as const,
      appearance: 'soft' as const,
      class: 'bg-destructive/10 border-destructive/20 text-destructive',
    },

    // ── Outline ────────────────────────────────────────────────────────────────
    {
      variant: 'default' as const,
      appearance: 'outline' as const,
      class: 'border-border text-foreground',
    },
    { variant: 'info' as const, appearance: 'outline' as const, class: 'border-info text-info' },
    {
      variant: 'success' as const,
      appearance: 'outline' as const,
      class: 'border-success text-success',
    },
    {
      variant: 'warning' as const,
      appearance: 'outline' as const,
      class: 'border-warning text-warning',
    },
    {
      variant: 'destructive' as const,
      appearance: 'outline' as const,
      class: 'border-destructive text-destructive',
    },

    // ── Ghost ──────────────────────────────────────────────────────────────────
    {
      variant: 'default' as const,
      appearance: 'ghost' as const,
      class: 'bg-muted/60 text-foreground',
    },
    { variant: 'info' as const, appearance: 'ghost' as const, class: 'bg-info/10 text-info' },
    {
      variant: 'success' as const,
      appearance: 'ghost' as const,
      class: 'bg-success/10 text-success',
    },
    {
      variant: 'warning' as const,
      appearance: 'ghost' as const,
      class: 'bg-warning/10 text-warning',
    },
    {
      variant: 'destructive' as const,
      appearance: 'ghost' as const,
      class: 'bg-destructive/10 text-destructive',
    },
  ],

  defaultVariants: {
    variant: 'default' as const,
    appearance: 'soft' as const,
  },
};

const alertVariants = cva(alertBase, alertVariantConfig);

// ─── Props ────────────────────────────────────────────────────────────────────

export interface AlertProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof alertVariants> {
  /** Left-side icon slot — accepts any ReactNode (Lucide icon, SVG, etc.) */
  icon?: React.ReactNode;
  /**
   * Shows a × button and removes the alert from the DOM on click.
   * Fire `onDismiss` to be notified when this happens.
   */
  dismissible?: boolean;
  /**
   * Callback fired when the × button is clicked.
   * Also shows the × button when provided without `dismissible` — use this
   * for externally controlled visibility.
   */
  onDismiss?: React.MouseEventHandler<HTMLButtonElement>;
}

// ─── Alert ────────────────────────────────────────────────────────────────────

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  ({ className, variant, appearance, icon, dismissible, onDismiss, children, ...props }, ref) => {
    const [open, setOpen] = React.useState(true);

    if (!open) return null;

    const showDismissButton = dismissible || !!onDismiss;

    const handleDismiss: React.MouseEventHandler<HTMLButtonElement> = (e) => {
      if (dismissible) setOpen(false);
      onDismiss?.(e);
    };

    return (
      <div
        ref={ref}
        role="alert"
        className={cn(alertVariants({ variant, appearance }), className)}
        {...props}
      >
        <div className="flex gap-3">
          {icon && (
            <span className="mt-0.5 shrink-0 [&_svg]:size-4" aria-hidden="true">
              {icon}
            </span>
          )}
          <div className="flex-1 min-w-0">{children}</div>
          {showDismissButton && (
            <button
              type="button"
              aria-label="Dismiss"
              onClick={handleDismiss}
              className="shrink-0 -mt-0.5 -mr-1 ml-auto self-start rounded-[var(--radius)] opacity-70 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-current focus:ring-offset-1 transition-opacity"
            >
              <X className="size-4" />
            </button>
          )}
        </div>
      </div>
    );
  },
);
Alert.displayName = 'Alert';

// ─── AlertTitle ───────────────────────────────────────────────────────────────

export interface AlertTitleProps extends React.HTMLAttributes<HTMLDivElement> {}

const AlertTitle = React.forwardRef<HTMLDivElement, AlertTitleProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('mb-1 font-semibold leading-none tracking-tight', className)}
      {...props}
    />
  ),
);
AlertTitle.displayName = 'AlertTitle';

// ─── AlertDescription ─────────────────────────────────────────────────────────

export interface AlertDescriptionProps extends React.HTMLAttributes<HTMLParagraphElement> {}

const AlertDescription = React.forwardRef<HTMLParagraphElement, AlertDescriptionProps>(
  ({ className, ...props }, ref) => (
    <p ref={ref} className={cn('text-sm opacity-90 leading-relaxed', className)} {...props} />
  ),
);
AlertDescription.displayName = 'AlertDescription';

export { Alert, AlertDescription, AlertTitle, alertVariants };
